import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AssistedSession, AssistedSessionStarted, Office, VerifiedVia } from '../../lib/types';
import { ErrorNote, Field, StatusBadge } from '../../ui/components';

const PRESET_ACTIONS = ['Renewed national ID', 'Renewed passport', 'Assisted online application', 'Explained requirements'];

export function AssistedSessionPanel({ office }: { office: Office }) {
  const { persona } = useAuth();
  const canOperate = persona?.roles.includes('COUNTER_OFFICER') ?? false;

  const [counterId, setCounterId] = useState(`${office.code}-C1`);
  const [citizenNin, setCitizenNin] = useState('');
  const [verifiedVia, setVerifiedVia] = useState<VerifiedVia>('ID_SCAN');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const [started, setStarted] = useState<AssistedSessionStarted | null>(null);
  const [session, setSession] = useState<AssistedSession | null>(null);
  const [copied, setCopied] = useState(false);

  const [resumeId, setResumeId] = useState('');
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);

  const [customAction, setCustomAction] = useState('');
  const [note, setNote] = useState('');
  const [logError, setLogError] = useState<string | null>(null);
  const [logging, setLogging] = useState(false);
  const [ending, setEnding] = useState(false);

  const active = started ?? session;

  const start = async () => {
    setStarting(true);
    setStartError(null);
    try {
      const s = await api.post<AssistedSessionStarted>('/api/v1/servicedesk/assisted-sessions', {
        counterId,
        citizenNin,
        verifiedVia,
      });
      setStarted(s);
      setSession(null);
      setCopied(false);
    } catch (e) {
      setStartError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not start the session');
    } finally {
      setStarting(false);
    }
  };

  const resume = async () => {
    setResuming(true);
    setResumeError(null);
    try {
      const s = await api.get<AssistedSession>(`/api/v1/servicedesk/assisted-sessions/${resumeId.trim()}`);
      setSession(s);
      setStarted(null);
    } catch (e) {
      setResumeError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not find that session');
    } finally {
      setResuming(false);
    }
  };

  const logAction = async (action: string) => {
    if (!active || !action.trim()) return;
    setLogging(true);
    setLogError(null);
    try {
      const updated = await api.post<AssistedSession>(`/api/v1/servicedesk/assisted-sessions/${active.id}/log`, {
        action: action.trim(),
        detail: note.trim() ? { note: note.trim() } : undefined,
      });
      if (started) setStarted({ ...started, ...updated });
      else setSession(updated);
      setCustomAction('');
      setNote('');
    } catch (e) {
      setLogError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not log the action');
    } finally {
      setLogging(false);
    }
  };

  const end = async () => {
    if (!active) return;
    setEnding(true);
    setLogError(null);
    try {
      const updated = await api.post<AssistedSession>(`/api/v1/servicedesk/assisted-sessions/${active.id}/end`);
      if (started) setStarted({ ...started, ...updated });
      else setSession(updated);
    } catch (e) {
      setLogError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not end the session');
    } finally {
      setEnding(false);
    }
  };

  const copyToken = async () => {
    if (!started) return;
    try {
      await navigator.clipboard.writeText(started.impersonationToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — the token is still selectable text below
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="card p-5">
        <h2 className="mb-1 text-sm font-bold text-slate-700">Assisted counter session</h2>
        <p className="mb-3 text-xs text-slate-500">
          Officer is briefly acting on behalf of a presence-verified citizen. Starting a session issues a short-lived, HMAC-scoped
          impersonation token (30 min).
        </p>

        {!active ? (
          <div className="space-y-3">
            {startError ? <ErrorNote message={startError} /> : null}
            <Field label="Counter ID">
              <input className="field-input" value={counterId} onChange={(e) => setCounterId(e.target.value)} />
            </Field>
            <Field label="Citizen NIN">
              <input className="field-input" value={citizenNin} onChange={(e) => setCitizenNin(e.target.value)} placeholder="e.g. SC940822015" />
            </Field>
            <Field label="Presence verified via">
              <select className="field-input" value={verifiedVia} onChange={(e) => setVerifiedVia(e.target.value as VerifiedVia)}>
                <option value="ID_SCAN">ID scan</option>
                <option value="OTP">OTP</option>
              </select>
            </Field>
            <button
              className="btn-primary w-full"
              onClick={start}
              disabled={starting || !canOperate || !counterId.trim() || citizenNin.trim().length < 5}
            >
              {starting ? 'Starting…' : 'Start session'}
            </button>
            {!canOperate ? <p className="text-xs text-amber-600">Requires the Counter Officer role.</p> : null}

            <div className="border-t border-slate-100 pt-3">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Resume a session you started earlier</div>
              {resumeError ? <ErrorNote message={resumeError} /> : null}
              <div className="mt-1 flex gap-2">
                <input className="field-input" value={resumeId} onChange={(e) => setResumeId(e.target.value)} placeholder="session id…" />
                <button className="btn-secondary shrink-0" onClick={resume} disabled={resuming || !resumeId.trim()}>
                  {resuming ? '…' : 'Load'}
                </button>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                The impersonation token is shown only once, at creation — resuming shows the session's action log, not the token.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3.5 py-2.5">
              <div>
                <div className="text-sm font-semibold text-slate-900">{active.citizenNin}</div>
                <div className="text-xs text-slate-500">
                  Counter {active.counterId} · verified via <StatusBadge status={active.verifiedVia} />
                </div>
              </div>
              {active.endedAt ? <StatusBadge status="CLOSED" /> : <StatusBadge status="ACTIVE" />}
            </div>

            {started ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3.5">
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                  Scoped impersonation token — display only
                </div>
                <div className="mt-1 break-all rounded bg-white/70 px-2 py-1.5 font-mono text-[11px] text-slate-700">
                  {started.impersonationToken}
                </div>
                <div className="mt-1.5 flex items-center justify-between text-xs text-amber-700">
                  <span>Expires {formatDateTime(started.tokenExpiresAt)}</span>
                  <button className="font-semibold underline" onClick={copyToken}>
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="mt-1.5 text-[11px] text-amber-700/80">
                  HMAC-signed internal token scoped to this session — no other module consumes it yet.
                </p>
              </div>
            ) : null}

            {logError ? <ErrorNote message={logError} /> : null}

            {!active.endedAt ? (
              <>
                <div>
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Log an action</div>
                  <div className="flex flex-wrap gap-1.5">
                    {PRESET_ACTIONS.map((a) => (
                      <button key={a} className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => logAction(a)} disabled={logging}>
                        {a}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <input
                      className="field-input"
                      placeholder="Custom action…"
                      value={customAction}
                      onChange={(e) => setCustomAction(e.target.value)}
                    />
                    <button className="btn-secondary shrink-0" onClick={() => logAction(customAction)} disabled={logging || !customAction.trim()}>
                      Log
                    </button>
                  </div>
                  <input
                    className="field-input mt-2"
                    placeholder="Note (optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
                <button className="btn-danger w-full" onClick={end} disabled={ending}>
                  {ending ? 'Ending…' : 'End session'}
                </button>
              </>
            ) : (
              <button
                className="btn-secondary w-full"
                onClick={() => {
                  setStarted(null);
                  setSession(null);
                  setCitizenNin('');
                }}
              >
                Start another session
              </button>
            )}
          </div>
        )}
      </div>

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Actions log</h2>
        {!active || active.actionsLog.length === 0 ? (
          <div className="px-2 py-8 text-center text-sm text-slate-400">Nothing logged yet.</div>
        ) : (
          <ul className="space-y-2">
            {active.actionsLog.map((entry, i) => (
              <li key={i} className="rounded-lg border border-slate-200 px-3.5 py-2.5">
                <div className="text-sm font-medium text-slate-800">{entry.action}</div>
                {entry.detail && Object.keys(entry.detail).length > 0 ? (
                  <div className="mt-0.5 text-xs text-slate-500">{JSON.stringify(entry.detail)}</div>
                ) : null}
                <div className="mt-0.5 text-[11px] text-slate-400">{formatDateTime(entry.at)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
